import { useEffect, useState } from "react";
import {
    Lock, ExternalLink, RefreshCw, Copy, Check, ChevronDown, ChevronUp,
    Code2, Tag, Pencil, Loader2, AlertTriangle, ShoppingBag, Store,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PrimaryButton } from "@/components/design-system";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

/* ============================================================================
   SİSTEM > ENTEGRASYON > KURULUM
   ----------------------------------------------------------------------------
   4 kartlı yapı (2026-02-25 kullanıcı feedback):
     1. Bağlı Mağaza          — read-only. Değişiklik: hello@atamis.global
     2. Bağlantı Durumu       — SADECE test. Kod yok. Head + Meta bağlantı satırları.
     3. Head Embed Script     — kod bloğu + kurulum adımları + platform ipucu.
     4. Ürün Detay SKU Etiketi — kod bloğu + kurulum adımları + Test URL yönetimi
                                (max 3 URL, sıkı domain match).

   İleride sipariş onay scriptleri eklendiğinde: 5. kart + Bağlantı Durumu'na
   yeni satır ekleyerek büyüteceğiz.
============================================================================ */
const SUPPORT_EMAIL = "hello@atamis.global";
const MAX_URLS = 3;

export default function InstallationTab() {
    const { session } = useAuth();
    const isOwner = session?.user?.role === "owner";

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testingHead, setTestingHead] = useState(false);
    const [testingMeta, setTestingMeta] = useState(false);
    const [urlsDialogOpen, setUrlsDialogOpen] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/install/status");
            setStatus(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Durum yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const runTest = async (endpoint, setBusy) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/install/${endpoint}`);
            setStatus(data);
            const c = data?._check;
            if (endpoint === "test-all") {
                toast[c?.ok ? "success" : "error"](c?.ok ? "Bağlantı sağlandı" : "Bağlantı sorunu tespit edildi");
            } else if (endpoint === "test-head") {
                toast[c?.ok ? "success" : "error"](c?.reason || (c?.ok ? "Head script bulundu" : "Head script bulunamadı"));
            } else if (endpoint === "test-meta") {
                toast[c?.ok ? "success" : "error"](c?.reason || (c?.ok ? "SKU etiketi doğrulandı" : "SKU etiketi doğrulanamadı"));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test başarısız");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="rounded-xl border border-border bg-surface p-10 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Yükleniyor…
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="installation-tab">
            <StoreCard store={status.store} />

            <ConnectionStatusCard
                head={status.head}
                meta={status.meta}
                onTestHead={() => runTest("test-head", setTestingHead)}
                onTestMeta={() => runTest("test-meta", setTestingMeta)}
                testingHead={testingHead}
                testingMeta={testingMeta}
            />

            <HeadEmbedScriptCard snippet={status.head.snippet} statusKey={status.head.status} />

            <ProductMetaTagCard
                snippet={status.meta.snippet}
                statusKey={status.meta.status}
                urls={status.meta.product_urls}
                results={status.meta.results}
                isOwner={isOwner}
                onEditUrls={() => setUrlsDialogOpen(true)}
            />

            <ProductUrlsDialog
                open={urlsDialogOpen}
                onClose={() => setUrlsDialogOpen(false)}
                siteDomain={status.store.site_domain}
                initialUrls={status.meta.product_urls}
                onSaved={(next) => { setStatus(next); setUrlsDialogOpen(false); }}
            />
        </div>
    );
}

/* ============================================================================
   KART 1 — Bağlı Mağaza (read-only)
============================================================================ */
const StoreCard = ({ store }) => {
    const domain = (store?.site_domain || "").trim();
    const url = domain ? `https://${domain}` : "";

    return (
        <div className="rounded-xl border border-border bg-surface p-5 flex items-center gap-4" data-testid="store-card">
            <div className="h-11 w-11 rounded-lg bg-foreground/5 border border-border grid place-items-center shrink-0">
                <Store className="h-5 w-5 text-foreground/70" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" data-testid="store-company-name">
                    {store?.company_name || "Mağaza"}
                </div>
                {domain ? (
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-mono truncate mt-0.5"
                        data-testid="store-domain-link"
                    >
                        {domain} <ExternalLink className="h-3 w-3" />
                    </a>
                ) : (
                    <div className="text-[12px] text-muted-foreground">Domain ayarlanmadı</div>
                )}
            </div>
            <TooltipProvider delayDuration={100}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="shrink-0 h-8 w-8 rounded-md border border-border grid place-items-center text-muted-foreground cursor-help" data-testid="store-lock-icon">
                            <Lock className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                        <p className="text-xs leading-relaxed">
                            Mağaza bilgileri hesap yöneticiniz tarafından yapılandırıldı.
                            Değişiklik için <span className="font-mono">{SUPPORT_EMAIL}</span> ile iletişime geçin.
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
};

/* ============================================================================
   KART 2 — Bağlantı Durumu (sadece test)
   • Header: title + son otomatik kontrol + "Şimdi Test Et" primary
   • Body: 2 satır (Head + Meta) — sadece durum, snippet YOK
============================================================================ */
const ConnectionStatusCard = ({
    head, meta, onTestHead, onTestMeta,
    testingHead, testingMeta,
}) => {
    const lastCheck = pickMostRecent([head.last_at, meta.last_at]);

    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="connection-card">
            <div className="px-5 pt-5 pb-4 border-b border-border">
                <h3 className="font-display text-base font-semibold tracking-tight">Bağlantı Durumu</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                    Sitenizdeki kurulum kodlarının erişilebilir olduğunu doğrular · Son kontrol: <span className="font-mono">{fmtAgo(lastCheck)}</span>
                </p>
            </div>

            <StatusRow
                icon={Code2}
                title="Head Embed Script"
                subtitle={`Sitenizin <head> etiketinde bulunmalı`}
                statusKey={head.status}
                lastAt={head.last_at}
                reason={head.reason}
                onTest={onTestHead}
                testing={testingHead}
                testIdPrefix="head"
            />

            <div className="border-t border-border mx-5" />

            <StatusRow
                icon={Tag}
                title="Ürün Detay SKU Etiketi"
                subtitle={`Ürün detay sayfanızda bulunmalı`}
                statusKey={meta.status}
                lastAt={meta.last_at}
                reason={meta.reason}
                onTest={onTestMeta}
                testing={testingMeta}
                testIdPrefix="meta"
            />
        </div>
    );
};

const StatusRow = ({
    icon: Icon, title, subtitle, statusKey, lastAt, reason,
    onTest, testing, testIdPrefix,
}) => (
    <div className="px-5 py-4 flex items-center gap-3" data-testid={`row-${testIdPrefix}`}>
        <div className="h-9 w-9 rounded-md bg-muted/40 border border-border grid place-items-center shrink-0">
            <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{title}</div>
            <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                {subtitle} · Son doğrulama: <span className="font-mono">{fmtAgo(lastAt)}</span>
                {reason ? <span> · {reason}</span> : null}
            </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
            <StatusPill statusKey={statusKey} testId={`${testIdPrefix}-status`} />
            <Button
                variant="outline"
                size="sm"
                onClick={onTest}
                disabled={testing}
                className="h-8 gap-1.5 text-[11.5px]"
                data-testid={`${testIdPrefix}-test-btn`}
            >
                {testing
                    ? <><Loader2 className="h-3 w-3 animate-spin" /> Test ediliyor…</>
                    : <><RefreshCw className="h-3 w-3" /> Test Et</>}
            </Button>
        </div>
    </div>
);

const StatusPill = ({ statusKey, testId }) => {
    const map = {
        connected:    { label: "Bağlı",       cls: "border-success/40 bg-success-soft text-success" },
        disconnected: { label: "Bağlı Değil", cls: "border-destructive/40 bg-destructive/10 text-destructive" },
        unknown:      { label: "Henüz Test Edilmedi", cls: "border-border bg-muted/40 text-muted-foreground" },
    };
    const it = map[statusKey] || map.unknown;
    return (
        <div
            className={`inline-flex items-center h-8 rounded-md border px-3 text-xs font-medium ${it.cls}`}
            data-testid={testId}
        >
            {it.label}
        </div>
    );
};

/* ============================================================================
   KART 3 — Head Embed Script (kod + talimat)
============================================================================ */
const HeadEmbedScriptCard = ({ snippet, statusKey }) => (
    <SetupCard
        icon={Code2}
        title="Head Embed Script"
        description="Sayz widget'ının çalışabilmesi için sitenizin her sayfasının <head> etiketinin içine eklenmesi gereken zorunlu kod."
        statusKey={statusKey}
        testId="head-setup-card"
    >
        <CodeBlock code={snippet} lang="HTML" testId="head-snippet" />
    </SetupCard>
);

/* ============================================================================
   KART 4 — Ürün Detay SKU Etiketi (kod + talimat + Test URL yönetimi)
============================================================================ */
const ProductMetaTagCard = ({ snippet, statusKey, urls, results, isOwner, onEditUrls }) => {
    // Snippet'i placeholder highlight ile göstermek için parçala
    const highlightPlaceholder = (s) => {
        const m = s.match(/^(.*data-sayz-sku=")([^"]+)(".*)$/);
        if (!m) return <>{s}</>;
        return (
            <>
                {m[1]}
                <span className="text-warning font-semibold">{m[2]}</span>
                {m[3]}
            </>
        );
    };

    return (
        <SetupCard
            icon={Tag}
            title="Ürün Detay SKU Etiketi"
            description="Her ürün detay sayfasında o ürünün SKU/barkod bilgisini Sayz'a bildirir. Widget'ın doğru bedeni önerebilmesi için gereklidir. Body'nin her yerine konabilir — head erişimi gerekmez."
            statusKey={statusKey}
            testId="meta-setup-card"
        >
            <CodeBlock code={snippet} lang="HTML" testId="meta-snippet" render={highlightPlaceholder} />

            <TestUrlsPanel
                urls={urls}
                results={results}
                isOwner={isOwner}
                onEdit={onEditUrls}
            />
        </SetupCard>
    );
};

/* ============================================================================
   Ortak: SetupCard, LiveDot, CodeBlock
============================================================================ */
const SetupCard = ({ icon: Icon, title, description, testId, statusKey, children }) => (
    <div className="rounded-xl border border-border bg-surface" data-testid={testId}>
        <div className="px-5 pt-5 pb-4 border-b border-border flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-muted/40 border border-border grid place-items-center shrink-0 mt-0.5">
                <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5">
                    <h3 className="font-display text-base font-semibold tracking-tight">{title}</h3>
                    {statusKey ? <LiveDot statusKey={statusKey} /> : null}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed max-w-2xl">{description}</p>
            </div>
        </div>
        <div className="p-5 space-y-5">
            {children}
        </div>
    </div>
);

/* Bağlantı durumuna göre canlı gösterge — connected → yeşil pulse,
   disconnected → statik kırmızı, unknown → soluk gri statik. */
const LiveDot = ({ statusKey }) => {
    if (statusKey === "connected") {
        return (
            <span className="relative inline-flex h-2.5 w-2.5" title="Bağlı" data-testid="live-dot-connected">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
            </span>
        );
    }
    if (statusKey === "disconnected") {
        return <span className="inline-flex h-2.5 w-2.5 rounded-full bg-destructive" title="Bağlı değil" data-testid="live-dot-disconnected" />;
    }
    return <span className="inline-flex h-2.5 w-2.5 rounded-full bg-muted-foreground/30" title="Henüz test edilmedi" data-testid="live-dot-unknown" />;
};

const CodeBlock = ({ code, lang, testId, render }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    return (
        <div className="rounded-lg border border-border bg-[hsl(var(--muted))]/40 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-muted/60">
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{lang}</span>
                <button
                    type="button"
                    onClick={copy}
                    className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`${testId}-copy`}
                >
                    {copied
                        ? <><Check className="h-3 w-3 text-success" /> Kopyalandı</>
                        : <><Copy className="h-3 w-3" /> Kopyala</>}
                </button>
            </div>
            <pre className="px-3 py-3 text-[12px] font-mono leading-relaxed overflow-x-auto scroll-thin whitespace-pre-wrap break-all" data-testid={testId}>
                {render ? render(code) : code}
            </pre>
        </div>
    );
};

/* ============================================================================
   Test URL'leri paneli (Kart 4 içinde)
============================================================================ */
const TestUrlsPanel = ({ urls = [], results = [], isOwner, onEdit }) => {
    const [expanded, setExpanded] = useState(urls.length > 0);
    return (
        <div className="rounded-md border border-border bg-background" data-testid="test-urls-panel">
            <div className="flex items-center justify-between gap-3 px-3.5 py-3 border-b border-border">
                <div className="flex items-center gap-2 min-w-0">
                    <ShoppingBag className="h-3.5 w-3.5 text-muted-foreground shrink-0" strokeWidth={1.75} />
                    <div className="min-w-0">
                        <div className="text-[12.5px] font-medium">Test URL{"'"}leri</div>
                        <div className="text-[10.5px] text-muted-foreground mt-0.5">
                            {urls.length === 0
                                ? "SKU etiketini test etmek için 3 örnek ürün URL'i ekleyin"
                                : `${urls.length}/${MAX_URLS} URL · saatte bir otomatik test edilir`}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {urls.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            className="h-7 w-7 rounded-md border border-border grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            title={expanded ? "Kapat" : "URL'leri göster"}
                            data-testid="urls-toggle"
                        >
                            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        </button>
                    )}
                    {isOwner && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onEdit}
                            className="h-7 gap-1.5 text-[11px]"
                            data-testid="urls-edit-btn"
                        >
                            <Pencil className="h-3 w-3" /> {urls.length ? "Düzenle" : "URL Ekle"}
                        </Button>
                    )}
                </div>
            </div>

            {expanded && urls.length > 0 && (
                <ul className="divide-y divide-border" data-testid="urls-list">
                    {urls.map((u) => {
                        const r = results.find((x) => x.url === u);
                        const found = r?.found;
                        return (
                            <li key={u} className="flex items-center gap-2 px-3.5 py-2.5">
                                <span
                                    className="h-1.5 w-1.5 rounded-full shrink-0"
                                    style={{ background: found ? "hsl(var(--success))" : r ? "hsl(var(--destructive))" : "hsl(var(--muted-foreground))" }}
                                />
                                <a
                                    href={u}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[11px] font-mono truncate text-muted-foreground hover:text-foreground min-w-0 flex-1"
                                >
                                    {u}
                                </a>
                                {r?.error && <span className="text-destructive text-[10px] shrink-0">{r.error}</span>}
                                {found && r?.sku && (
                                    <span className={`text-[10px] shrink-0 font-mono ${r.sku_matched ? "text-success" : "text-warning"}`}>
                                        SKU: {r.sku}{r.sku_matched ? " ✓" : " (kataloğunda yok)"}
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

/* ============================================================================
   Ürün URL düzenleme dialog
============================================================================ */
const ProductUrlsDialog = ({ open, onClose, siteDomain, initialUrls, onSaved }) => {
    const [urls, setUrls] = useState(["", "", ""]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        const padded = [...(initialUrls || [])];
        while (padded.length < MAX_URLS) padded.push("");
        setUrls(padded.slice(0, MAX_URLS));
    }, [open, initialUrls]);

    const validate = (u) => {
        const raw = (u || "").trim();
        if (!raw) return { ok: true, msg: "" };
        try {
            const parsed = new URL(raw);
            if (!/^https?:$/.test(parsed.protocol)) return { ok: false, msg: "Sadece https:// veya http:// başlayan URL'ler" };
            const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
            const domain = (siteDomain || "").toLowerCase().replace(/^www\./, "");
            const match = host === domain || host.endsWith("." + domain);
            return match
                ? { ok: true, msg: "" }
                : { ok: false, msg: `URL {"'"}${domain}{"'"} sitenize ait olmalı` };
        } catch {
            return { ok: false, msg: "Geçersiz URL formatı" };
        }
    };

    const errors = urls.map(validate);
    const hasError = errors.some((e) => !e.ok);
    const nonEmpty = urls.filter((u) => u.trim()).length;

    const save = async () => {
        if (hasError) { toast.error("Bazı URL'ler geçersiz"); return; }
        if (nonEmpty === 0) { toast.error("En az 1 URL girin"); return; }
        setSaving(true);
        try {
            const { data } = await api.put("/install/product-urls", {
                urls: urls.map((u) => u.trim()).filter(Boolean),
            });
            toast.success("Test URL'leri kaydedildi");
            onSaved(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg" data-testid="urls-dialog">
                <DialogHeader>
                    <DialogTitle>Test URL{"'"}lerini Düzenle</DialogTitle>
                    <DialogDescription>
                        Ürün detay SKU etiketini düzenli test etmek için 3 örnek URL ekleyin.
                        Sadece <span className="font-mono">{siteDomain}</span> sitenize ait URL{"'"}ler kabul edilir.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    {urls.map((u, i) => (
                        <div key={i}>
                            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                Ürün URL{"'"}i #{i + 1}
                            </label>
                            <Input
                                value={u}
                                onChange={(e) => setUrls((arr) => arr.map((x, idx) => idx === i ? e.target.value : x))}
                                placeholder={`https://${siteDomain}/urun/…`}
                                className={`h-9 text-sm font-mono mt-1 ${!errors[i].ok ? "border-destructive focus-visible:ring-destructive" : ""}`}
                                data-testid={`url-input-${i}`}
                            />
                            {!errors[i].ok && (
                                <p className="text-[11px] text-destructive mt-1 flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3" /> {errors[i].msg}
                                </p>
                            )}
                        </div>
                    ))}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving} data-testid="urls-cancel-btn">
                        İptal
                    </Button>
                    <PrimaryButton onClick={save} disabled={saving || hasError} data-testid="urls-save-btn">
                        {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Kaydediliyor…</> : "Kaydet"}
                    </PrimaryButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/* --- Utils ----------------------------------------------------------------- */
function pickMostRecent(arr) {
    return arr.filter(Boolean).sort().reverse()[0] || null;
}

function fmtAgo(iso) {
    if (!iso) return "hiç";
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const secs = Math.max(1, Math.floor((now - then) / 1000));
        if (secs < 60) return `${secs} sn önce`;
        const mins = Math.floor(secs / 60);
        if (mins < 60) return `${mins} dk önce`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs} sa önce`;
        const days = Math.floor(hrs / 24);
        return `${days} gün önce`;
    } catch { return "—"; }
}
